import { useMutation } from '@tanstack/react-query'
import { BadgeCheck, MoreVertical } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api, errorText } from '../api.ts'
import { Button } from '../components/Button.tsx'
import { Dropdown, DropdownItem } from '../components/Dropdown.tsx'
import { SearchBox } from '../components/SearchBox.tsx'
import { Tooltip } from '../components/Tooltip.tsx'
import { initials } from '../components/UserProfileDropdown.tsx'
import { useCompany, useCompanyCache } from '../company.ts'
import type { CatalogApp, Person } from '../types.ts'
import { AppMark, Badge, byKind, itemName, Loading, Modal, Notice, useMarkModalEdited } from '../ui.tsx'

const date = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

/** Why the company doesn't have an app. */
function whyNot(app: CatalogApp): string {
  if (app.status === 'off') {
    const when = app.changedAt ? ` on ${date.format(new Date(app.changedAt))}` : ''
    return `${app.name} was switched off for your company${when}. Contact Icefield to switch it back on.`
  }
  return `Your company doesn't have ${app.name}. Contact Icefield to add it.`
}

/** Every Icefield app, and then every MCP server, with how many of the company's users hold
 *  a license to each. One the company has can have its licenses managed from its menu; the
 *  ones it doesn't have are greyed out, and say why on hover. */
export function Licenses({ companyId }: { companyId: string }) {
  const detail = useCompany(companyId)
  const [managing, setManaging] = useState<CatalogApp | null>(null)
  if (detail.isPending) return <Loading />
  if (detail.isError) return <Notice>{errorText(detail.error)}</Notice>
  const { appCatalog, people } = detail.data
  // The company's own apps first, then the rest, each alphabetically (as ICE sends them).
  const apps = [...appCatalog].sort((a, b) => Number(b.status === 'on') - Number(a.status === 'on'))

  return (
    <section>
      <div className="section-head">
        <div>
          <h1>Licenses</h1>
          <p className="muted">Contact Icefield AI to add an agent to your environment.</p>
        </div>
      </div>
      {byKind(apps).map((group) => (
        <div key={group.kind}>
          {group.kind === 'mcp' && <h2 className="license-group">{group.label}</h2>}
          <ul className="list-rows card-list license-list">
            {group.items.map((app) => {
              const holders = people.filter((p) => p.apps.includes(app.appId)).length
              if (app.status !== 'on') {
                return (
                  <li key={app.appId} className="license-off">
                    <span className="license-row">
                      <Tooltip text={whyNot(app)} placement="right">
                        <span className="license-name">
                          <AppMark appId={app.appId} name={app.name} kind={app.kind} size={56} />
                          <span className="license-text">
                            <strong>{itemName(app)}</strong>
                            <span className="muted small">Not included</span>
                          </span>
                        </span>
                      </Tooltip>
                    </span>
                    <span className="license-count" />
                    <span className="license-menu-space" />
                  </li>
                )
              }
              return (
                <li key={app.appId}>
                  <span className="license-row">
                    <span className="license-name">
                      <AppMark appId={app.appId} name={app.name} kind={app.kind} size={56} />
                      <span className="license-text">
                        <strong>{itemName(app)}</strong>
                      </span>
                    </span>
                  </span>
                  <span className="license-count">
                    <strong>{holders}</strong> of {people.length} {people.length === 1 ? 'user' : 'users'}
                  </span>
                  <Dropdown
                    align="end"
                    trigger={({ open, toggle }) => (
                      <button type="button" className="icon-button icon-button-plain" onClick={toggle} aria-expanded={open} aria-haspopup="menu" aria-label={`Actions for ${app.name}`}>
                        <MoreVertical aria-hidden="true" />
                      </button>
                    )}
                  >
                    <DropdownItem icon={BadgeCheck} onClick={() => setManaging(app)}>Manage licenses</DropdownItem>
                  </Dropdown>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
      <Modal title={managing ? `${itemName(managing)} licenses` : 'Licenses'} open={managing !== null} onClose={() => setManaging(null)}>
        {managing && <ManageLicenses companyId={companyId} app={managing} users={people} onDone={() => setManaging(null)} />}
      </Modal>
    </section>
  )
}

/** Every user, searchable, with a switch for this app's license. Switches and Select all only
 *  change what's shown; Done saves the difference, one user at a time, and the X discards it.
 *  The list scrolls inside the dialog, so the dialog itself never does. */
function ManageLicenses({ companyId, app, users, onDone }: { companyId: string; app: CatalogApp; users: Person[]; onDone: () => void }) {
  const cache = useCompanyCache(companyId)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  // Who will hold the license once Done is pressed.
  const [chosen, setChosen] = useState(() => new Set(users.filter((u) => u.apps.includes(app.appId)).map((u) => u.id)))
  const save = useMutation({
    mutationFn: async () => {
      for (const user of users) {
        const enabled = chosen.has(user.id)
        if (user.apps.includes(app.appId) === enabled) continue
        // Each answer goes in the cache as it comes, so a failure part-way leaves the
        // page showing what was saved.
        cache.putPerson(await api.put<Person>(`/companies/${companyId}/people/${user.id}/apps/${app.appId}`, { enabled }))
      }
    },
    onMutate: () => setError(null),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  })
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (q ? users.filter((u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)) : users)
      .slice()
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }))
  }, [users, query])
  const allShownOn = shown.length > 0 && shown.every((u) => chosen.has(u.id))
  const markEdited = useMarkModalEdited()
  const set = (ids: string[], on: boolean) => {
    markEdited()
    setChosen((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  return (
    <div className="form">
      <p className="muted">
        {chosen.size} of {users.length} {users.length === 1 ? 'user' : 'users'} will have a license to {app.name}. Changes are saved when you press Done.
      </p>
      <Notice>{error}</Notice>
      <SearchBox value={query} onChange={setQuery} placeholder="Search users" width="100%" />
      {users.length === 0 ? (
        <p className="muted">No users yet.</p>
      ) : shown.length === 0 ? (
        <p className="muted license-users-empty">Nobody matches “{query.trim()}”.</p>
      ) : (
        <>
          <div className="select-all">
            <Button variant="tertiary" size="small" disabled={save.isPending} onClick={() => set(shown.map((u) => u.id), !allShownOn)}>
              {allShownOn ? 'Deselect all' : 'Select all'}
              {query.trim() ? ` ${shown.length} shown` : ''}
            </Button>
          </div>
          <ul className="list-rows license-users">
            {shown.map((u) => (
              <li key={u.id}>
                <span className="avatar" aria-hidden="true">{initials(u.displayName)}</span>
                <label className="user-text" htmlFor={`license-${u.id}`}>
                  <span className="user-name">
                    <strong>{u.displayName}</strong>
                    {u.status === 'disabled' && <Badge tone="warn">Disabled</Badge>}
                  </span>
                  <span className="muted small">{u.email}</span>
                </label>
                <input
                  id={`license-${u.id}`}
                  type="checkbox"
                  role="switch"
                  className="switch"
                  checked={chosen.has(u.id)}
                  disabled={save.isPending}
                  onChange={(e) => set([u.id], e.target.checked)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="form-actions">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>Done</Button>
      </div>
    </div>
  )
}
